import { useLanguage } from '../context/LanguageContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';

const SCREENSHOTS = [
  'https://customer-assets.emergentagent.com/job_satya-self/artifacts/77xe9o1v_IMG_2483.jpeg',
  'https://customer-assets.emergentagent.com/job_satya-self/artifacts/ts3dsiaj_8A467F74-B5BE-4520-9383-8B5A6AF1951D.jpeg',
  'https://customer-assets.emergentagent.com/job_satya-self/artifacts/6q7uecnd_BC3AC634-3FDC-4696-A15E-2BA1527981F4.jpeg',
  '/review6.jpg',
  '/review7.jpg',
];

const TestimonialsSection = () => {
  const { language } = useLanguage();
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const goTo = useCallback((index) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrent(index);
    setTimeout(() => setIsAnimating(false), 400);
  }, [isAnimating]);

  const next = useCallback(() => {
    goTo((current + 1) % SCREENSHOTS.length);
  }, [current, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + SCREENSHOTS.length) % SCREENSHOTS.length);
  }, [current, goTo]);

  // Auto-advance every 5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % SCREENSHOTS.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section 
      className="py-20 md:py-28"
      style={{ backgroundColor: 'var(--sage-50)' }}
      data-testid="testimonials-section"
    >
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        {/* Section Title */}
        <div className="text-center mb-12 md:mb-16">
          <p 
            className="text-sm font-medium tracking-widest uppercase mb-3"
            style={{ color: 'var(--sage-600)' }}
          >
            {language === 'he' ? 'מה אומרים המתאמנים' : 'What Clients Say'}
          </p>
          <h2 
            className="text-3xl md:text-4xl"
            style={{ fontFamily: 'Cormorant Garamond, serif', color: 'var(--charcoal)' }}
          >
            {language === 'he' ? 'סיפורים של שינוי אמיתי' : 'Stories of Real Transformation'}
          </h2>
        </div>

        {/* Carousel */}
        <div className="relative flex items-center justify-center gap-4" dir="ltr">
          {/* Prev Button */}
          <button
            onClick={prev}
            className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white shadow-md flex items-center justify-center transition-all hover:shadow-lg hover:scale-105"
            style={{ border: '1px solid var(--sand-200)' }}
            data-testid="testimonial-prev"
          >
            <ChevronLeft size={20} style={{ color: 'var(--charcoal)' }} />
          </button>

          {/* Screenshot Display - single image with fade */}
          <div 
            className="relative overflow-hidden rounded-3xl shadow-xl bg-white"
            style={{ 
              maxWidth: '360px', 
              width: '100%',
              border: '1px solid var(--sand-200)',
            }}
          >
            {SCREENSHOTS.map((src, index) => (
              <img
                key={index}
                src={src}
                alt={`Client review ${index + 1}`}
                className="w-full object-cover transition-opacity duration-400"
                style={{ 
                  display: current === index ? 'block' : 'none',
                  opacity: current === index ? 1 : 0,
                }}
                data-testid={`testimonial-img-${index}`}
              />
            ))}
          </div>

          {/* Next Button */}
          <button
            onClick={next}
            className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white shadow-md flex items-center justify-center transition-all hover:shadow-lg hover:scale-105"
            style={{ border: '1px solid var(--sand-200)' }}
            data-testid="testimonial-next"
          >
            <ChevronRight size={20} style={{ color: 'var(--charcoal)' }} />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-6">
          {SCREENSHOTS.map((_, index) => (
            <button
              key={index}
              onClick={() => goTo(index)}
              className="w-2.5 h-2.5 rounded-full transition-all duration-300"
              style={{ 
                backgroundColor: current === index ? 'var(--sage-600)' : 'var(--sand-300)',
                transform: current === index ? 'scale(1.3)' : 'scale(1)',
              }}
              data-testid={`testimonial-dot-${index}`}
            />
          ))}
        </div>

        {/* Note */}
        <p className="text-center text-sm text-gray-400 mt-6">
          {language === 'he' 
            ? '* מתוך שיחות WhatsApp אמיתיות עם מתאמנים. השמות הוסרו לשמירת פרטיות.'
            : '* From real WhatsApp conversations with clients. Names removed for privacy.'
          }
        </p>
      </div>
    </section>
  );
};

export default TestimonialsSection;
